import { IonContent, IonItem, IonList } from '@ionic/react';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { getStateDateAsString, getStateMomentAsString } from '../../common/util';
import { DashboardSelectedTab } from '../../components/dashboardFooter/DashboardFooter';
import { AttachmentAssetType, IAttachment, IAttachmentContainer } from '../../models/dms/attachment.model';
import { ClockingDirection, ClockingType, IAttendanceClocking, ILabourClocking } from '../../models/dms/clocking.model';
import { IContact, IRepairOrder, IRepairOrderSyncStatus, IVehicle } from '../../models/dms/repairOrder.model';
import { IRepairOrderJob } from '../../models/dms/repairOrderJob.model';
import { ITechComment } from '../../models/dms/technicianComments.model';
import { IWorkshopVehicleDetails } from '../../models/dms/workshopVehicle.model';
import { IJwt } from '../../models/portal/auth.model';
import { IDatabase } from '../../models/portal/database.model';
import { RouteNames } from '../../models/routes.model';
import store from '../../store';
import { selectDemoDateTime, setDemoDateTime, setIsDemoState, setIsInActionState, setShowMainMenu } from '../../store/appSlice';
import { setTokenState, signOutAsync } from '../../store/authSlice';
import { setSelectedDatabaseState } from '../../store/databaseSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setRepairOrderSyncState } from '../../store/repairOrderSyncSlice';
import { setDistanceTrackingUserActivatedState, setGpsServicesAvailableState } from '../../store/settingsSlice';
import { setAttendanceClockingState, setLabourClockingState } from '../../store/technicianClockingSlice';
import { setSelectedTechnicianCommentsState } from '../../store/technicianCommentSlice';
import { setSelectedVehicleAttachmentsState } from '../../store/vehicleAttachmentSlice';
import { setSelectedWorkshopVehicleState } from '../../store/workshopVehicleSlice';
import AdhocClockingPage from '../adhocClockingPage/AdhocClockingPage';
import ClockingDetailPage from '../clockingDetailPage/ClockingDetailPage';
import DashboardPage from '../dashboardPage/DashboardPage';
import CompanyPage from '../signinPage/companyPage/CompanyPage';
import { IonPageWrapper } from './DemoPage.styles';

interface IComponentProps {}

enum ModuleView {
  AppSignIn,
  AppTmcDashboard,
  AppJobSearch,
  AppLabourClockingDetail,
  AppAdhocJobDetail,
}

const DemoPage: React.FC<IComponentProps> = () => {
  const [showMenu, setShowMenu] = useState(true);
  const [currentModule, setCurrentModule] = useState<ModuleView>(ModuleView.AppTmcDashboard);
  const [defaultDate] = useState(moment().set({ hour: 10, minute: 30, second: 32 }).toDate());

  const history = useHistory();
  const { token } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setIsDemoState(true));
    dispatch(setIsInActionState(true));
    dispatch(setTokenState({ ...token, displayUsername: 'Phillip Fry' } as IJwt));
    dispatch(setSelectedDatabaseState({ dbId: 1, dbDisplayName: 'Sandbox' } as IDatabase));
    return () => {
      dispatch(setIsDemoState(false));
      dispatch(setIsInActionState(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClickFrame = () => {
    setShowMenu((prev) => {
      const newShowMenu = !prev;
      if (newShowMenu) {
        dispatch(setShowMainMenu(true));
      }
      return newShowMenu;
    });
  };

  const signIn = () => {
    // Sign in
    if (showMenu) {
      setCurrentModule(ModuleView.AppSignIn);
      dispatch(setShowMainMenu(false));
    }
  };
  const clockedOffAll = async () => {
    // All clocking off
    if (showMenu) {
      setCurrentModule(ModuleView.AppTmcDashboard);
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.Off),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.Off)),
        }),
      );
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.ClockingSummary}`,
      });
    }
  };
  const clockedOnAttendance = async () => {
    // Attendance clocking on
    if (showMenu) {
      setCurrentModule(ModuleView.AppTmcDashboard);
      dispatch(setDemoDateTime(getStateDateAsString(defaultDate)));
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.On, moment().set({ hour: 6, minute: 32, second: 44 }).toDate()),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.Off)),
        }),
      );
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.ClockingSummary}`,
      });
    }
  };
  const clockedOnAll = async () => {
    // Attendance / labour clocking on
    if (showMenu) {
      setCurrentModule(ModuleView.AppTmcDashboard);
      dispatch(setDemoDateTime(getStateDateAsString(defaultDate)));
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.On, moment().set({ hour: 6, minute: 32, second: 44 }).toDate()),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.On, false, 'S15K12MKC69434', 864155, moment().set({ hour: 10, minute: 23, second: 8 }).toDate())),
        }),
      );
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.ClockingSummary}`,
      });
    }
  };
  const clockedOnAllWithDistance = async () => {
    // Attendance / labour clocking on with distance tracking
    if (showMenu) {
      setCurrentModule(ModuleView.AppTmcDashboard);
      dispatch(setDemoDateTime(getStateMomentAsString(moment(defaultDate).add(9, 'minute'))));
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.On, moment().set({ hour: 6, minute: 32, second: 44 }).toDate()),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.On, true, 'TRAVEL', 864155, moment().set({ hour: 10, minute: 23, second: 8 }).toDate())),
        }),
      );
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.ClockingSummary}`,
      });
    }
  };
  const clockingBreak = async () => {
    // Attendance / labour clocking break
    if (showMenu) {
      setCurrentModule(ModuleView.AppTmcDashboard);
      dispatch(setDemoDateTime(getStateMomentAsString(moment(defaultDate).add({ minute: 9, second: 4 }))));
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.Break, moment().set({ hour: 6, minute: 32, second: 44 }).toDate()),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.Break, false, 'S15K12MKC69434', 864155, moment().set({ hour: 10, minute: 23, second: 8 }).toDate())),
        }),
      );
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.ClockingSummary}`,
      });
    }
  };
  const jobSearch = () => {
    // Job search
    if (showMenu) {
      setCurrentModule(ModuleView.AppJobSearch);
      dispatch(setRepairOrderSyncState(defaultRepairOrderSyncState()));
      history.push({
        pathname: RouteNames.demo,
        search: `tab=${DashboardSelectedTab.JobSearch}`,
      });
    }
  };
  const clockingDetails = async () => {
    // Clocking details
    if (showMenu) {
      setCurrentModule(ModuleView.AppLabourClockingDetail);
      dispatch(setDemoDateTime(getStateMomentAsString(moment(defaultDate))));
      dispatch(
        setAttendanceClockingState({
          ...defaultAttendanceClocking(ClockingDirection.On, moment().set({ hour: 6, minute: 32, second: 44 }).toDate()),
        }),
      );
      dispatch(
        setLabourClockingState({
          ...(await defaultLabourClocking(ClockingDirection.On, false, 'S15K12MKC69434', 864155, moment().set({ hour: 10, minute: 23, second: 8 }).toDate())),
        }),
      );

      dispatch(
        setRepairOrderSyncState({
          data: [
            {
              ...defaultRepairOrderwithJobs(
                {
                  code: 'pattersonn',
                  name: 'Christina Webb',
                  companyName: '',
                  address: '',
                  businessPhone: '',
                  mobilePhone: '',
                  privatePhone: '',
                },
                '1',
                864155,
                'R',
                'S15K12MKC69434',
                '000-000',
                'VA',
                selectDemoDateTime(store.getState()),
              ),
              jobs: [{ ...defaultJob('1', 864155, 'R', 'TRAVEL') }, { ...defaultJob('1', 864155, 'R', 'S15K12MKC69434') }] as IRepairOrderJob[],
            },
          ] as IRepairOrder[],
        } as IRepairOrderSyncStatus),
      );

      dispatch(
        setSelectedWorkshopVehicleState({
          registrationNumber: '000-000',
          driver: { code: 'pattersonn', name: 'Christina Webb' },
          owner: { code: 'pattersonn', name: 'Christina Webb' },
          make: 'Kia',
          model: { code: 'Sorento', name: 'Sorento' },
          odometer: { reading: 4808, units: '' },
        } as IWorkshopVehicleDetails),
      );
      dispatch(
        setSelectedVehicleAttachmentsState({
          assetType: AttachmentAssetType.WorkshopVehicle,
          key: '864155',
          attachments: [{ demoUrl: '/assets/demo-car-1.jpg' }, { demoUrl: '/assets/demo-car-2.jpg' }, { demoUrl: '/assets/demo-car-3.jpg' }] as IAttachment[],
        } as IAttachmentContainer),
      );
      dispatch(
        setSelectedTechnicianCommentsState([
          { ...defaultTechComment('Service completed moving onto the repair.', '1', true, false, 864155, selectDemoDateTime(store.getState())) },
          { ...defaultTechComment('Brakes and tyres are running very low and will need replacing in 20,000km.', '1', false, true, 864155, selectDemoDateTime(store.getState())) },
          { ...defaultTechComment('Found an oil leak on the main rear seal.', '1', false, false, 864155, selectDemoDateTime(store.getState())) },
          { ...defaultTechComment('Wipers might need replacing at 20,000km.', '1', false, false, 864155, selectDemoDateTime(store.getState())) },
        ]),
      );
    }
  };
  const adhocJob = () => {
    // Adhoc job
    if (showMenu) {
      setCurrentModule(ModuleView.AppAdhocJobDetail);
    }
  };
  const onExitClick = () => {
    // Exit
    if (showMenu) {
      dispatch(signOutAsync());
    }
  };

  const defaultAttendanceClocking = (direction: ClockingDirection, date: Date = selectDemoDateTime(store.getState())) => {
    const dateString = getStateDateAsString(date);
    return {
      direction: direction,
      type: ClockingType.Attendance,
      startDateTime: dateString,
      endDateTime: direction === ClockingDirection.On ? null : getStateDateAsString(selectDemoDateTime(store.getState())),
    } as IAttendanceClocking;
  };

  const defaultLabourClocking = async (direction: ClockingDirection, withDistance: boolean = false, jobCode: string = 'S15K12MKC69434', repairOrderNumber: number = 864155, date: Date = selectDemoDateTime(store.getState())) => {
    const dateString = getStateDateAsString(date);

    if (withDistance) {
      dispatch(setDistanceTrackingUserActivatedState(true));
      dispatch(setGpsServicesAvailableState(true));
    } else {
      dispatch(setDistanceTrackingUserActivatedState(false));
      dispatch(setGpsServicesAvailableState(false));
    }

    return {
      direction: direction,
      type: ClockingType.Labour,
      startDateTime: dateString,
      endDateTime: direction === ClockingDirection.On ? null : getStateDateAsString(selectDemoDateTime(store.getState())),
      jobData: {
        branchCode: '1',
        repairOrderNumber: repairOrderNumber,
        jobType: 'R',
        jobCode: jobCode,
      },
      fromServer: false,
      repairOrder: {
        branch: '1',
        repairOrderNumber: repairOrderNumber,
        vehicle: defaultWorkshopVehicle('WKV'),
        registrationNumber: '000-000',
      },
    } as ILabourClocking;
  };

  const defaultRepairOrderSyncState = () => {
    return {
      data: [
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'pattersonn',
              name: 'Christina Webb',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864155,
            'R',
            'S15K12MKC69434',
            '000-000',
            'VA',
            selectDemoDateTime(store.getState()),
          ),
        },
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'masons',
              name: 'Shawn Mason',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864156,
            'R',
            'S15K12MKC69434',
            '000-000',
            'BI',
            moment(selectDemoDateTime(store.getState())).add(30, 'minute').toDate(),
          ),
        },
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'richardsv',
              name: 'Virgil Richards',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864158,
            'R',
            'S15K12MKC69434',
            '000-000',
            'BI',
            moment(selectDemoDateTime(store.getState())).add(45, 'minute').toDate(),
          ),
        },
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'olsonf',
              name: 'Felicia Olson',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864159,
            'R',
            'S15K12MKC69434',
            '000-000',
            'BI',
            moment(selectDemoDateTime(store.getState())).add(60, 'minute').toDate(),
          ),
        },
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'webbc',
              name: 'Christerpher Patterson',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864162,
            'R',
            'S15K12MKC69434',
            '000-000',
            'BI',
            moment(selectDemoDateTime(store.getState())).add(90, 'minute').toDate(),
          ),
        },
        {
          ...defaultRepairOrderwithJobs(
            {
              code: 'colemanm',
              name: 'Morris Coleman',
              companyName: '',
              address: '',
              businessPhone: '',
              mobilePhone: '',
              privatePhone: '',
            },
            '1',
            864163,
            'R',
            'S15K12MKC69434',
            '000-000',
            'BI',
            moment(selectDemoDateTime(store.getState())).add(120, 'minute').toDate(),
          ),
        },
      ],
      hasSync: true,
      isProcessing: false,
    } as IRepairOrderSyncStatus;
  };

  const defaultRepairOrderwithJobs = (
    contact: IContact,
    branch: string = '1',
    repairOrderNumber: number = 864155,
    jobType: string = 'R',
    jobCode: string = 'S15K12MKC69434',
    rego: string = '000-000',
    progress: string = 'BI',
    date: Date = selectDemoDateTime(store.getState()),
  ) => {
    return {
      repairOrderNumber: repairOrderNumber,
      branch: branch,
      driver: contact,
      dropOffDateTime: getStateMomentAsString(moment(date)),
      isClosed: false,
      isConfirmed: true,
      isInvoiced: false,
      isQuotation: false,
      isAdhoc: false,
      isVetted: true,
      owner: contact,
      pickUpDateTime: getStateMomentAsString(moment(date).add(1, 'hour')),
      progressStatus: { code: progress, description: progress },
      registrationNumber: rego,
      jobs: [{ ...defaultJob(branch, repairOrderNumber, jobType, jobCode) }],
      vehicle: {
        description: '',
        registration: rego,
        source: 'WKV',
        sourceRef: rego,
      },
    } as IRepairOrder;
  };

  const defaultJob = (branch: string = '1', repairOrderNumber: number = 864155, jobType: string = 'R', jobCode: string = 'S15K12MKC69434') => {
    return {
      repairOrderNumber: repairOrderNumber,
      branchCode: branch,
      jobType: jobType,
      jobCode: jobCode,
    } as IRepairOrderJob;
  };

  const defaultTechComment = (comment: string, branch: string, actioned: boolean, isAlert: boolean, repairOrderNumber: number, createdDateTime: Date = selectDemoDateTime(store.getState())) => {
    return {
      comment: comment,
      branch: branch,
      actioned: actioned,
      isAlert: isAlert,
      createdDateTime: getStateDateAsString(createdDateTime),
      repairOrderNumber: repairOrderNumber,
    } as ITechComment;
  };

  const defaultWorkshopVehicle = (source: string) => {
    return {
      source,
    } as IVehicle;
  };

  return (
    <IonPageWrapper>
      <IonContent>
        <IonList className={`overlay ${showMenu ? '' : 'hidden'}`} onClick={() => onClickFrame()}>
          <IonItem onClick={() => signIn()}>Sign In</IonItem>
          <IonItem onClick={() => clockedOffAll()}>Tech Mobile Dashboard - Clocked off all</IonItem>
          <IonItem onClick={() => clockedOnAttendance()}>Tech Mobile Dashboard - Clocked in attendance</IonItem>
          <IonItem onClick={() => clockedOnAll()}>Tech Mobile Dashboard - Clocked on labour</IonItem>
          <IonItem onClick={() => clockedOnAllWithDistance()}>Tech Mobile Dashboard - Clocked on labour with distance</IonItem>
          <IonItem onClick={() => clockingBreak()}>Tech Mobile Dashboard - Clocking break</IonItem>
          <IonItem onClick={() => jobSearch()}>Job Search</IonItem>
          <IonItem onClick={() => clockingDetails()}>Clocking Details</IonItem>
          <IonItem onClick={() => adhocJob()}>Adhoc Job</IonItem>
          <IonItem onClick={() => onExitClick()}>Exit</IonItem>
        </IonList>

        {(() => {
          switch (currentModule) {
            case ModuleView.AppSignIn:
              return <CompanyPage />;
            case ModuleView.AppTmcDashboard:
              return <DashboardPage />;
            case ModuleView.AppJobSearch:
              return <DashboardPage />;
            case ModuleView.AppLabourClockingDetail:
              return <ClockingDetailPage />;
            case ModuleView.AppAdhocJobDetail:
              return <AdhocClockingPage />;
            default:
              return <></>;
          }
        })()}
      </IonContent>
    </IonPageWrapper>
  );
};

export default DemoPage;
